import { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Calendar } from '../ui/calendar';
import { disablePastDates, makeDisableBeforeDateChargement } from '../../lib/dateConstraints';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import { Loader2, CalendarIcon, Clock, MapPin, Truck, User, Globe, FileText, CheckCircle2, CircleDot, Mail, Send, Paperclip } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { AddressAutocompleteInput } from '../prospects/AddressAutocompleteInput';
import { VisiteAnnulerDialog } from './VisiteAnnulerDialog';
import { useEditLock } from '../../hooks/useEditLock';
import { LockBanner } from '../permissions/LockBanner';
import { ReadOnlyGuard } from '../permissions/ReadOnlyGuard';

const API = process.env.REACT_APP_BACKEND_URL;

const emptyAddress = () => ({
    date: null, adresse: '', etage: '', ascenseur: '', portage: '',
    autorisation_stationnement: null, monte_meuble: null
});

const VISITE_STATUS_CONFIG = {
    a_venir: { label: 'A venir', badgeClass: 'bg-blue-100 text-blue-700', borderClass: 'border-blue-400' },
    commencee: { label: 'Commencer', badgeClass: 'bg-indigo-100 text-indigo-700', borderClass: 'border-indigo-400' },
    decalee: { label: 'A reprogrammer', badgeClass: 'bg-orange-100 text-orange-700', borderClass: 'border-orange-400' },
    annulee: { label: 'Annulee', badgeClass: 'bg-red-100 text-red-700', borderClass: 'border-red-400' },
    terminee: { label: 'Terminee', badgeClass: 'bg-emerald-100 text-emerald-700', borderClass: 'border-emerald-400' },
};

// Transitions de statut autorisées (alignées avec VisiteDetailDialog).
const VISITE_TRANSITIONS = {
    a_venir: ['commencee', 'decalee', 'annulee'],
    commencee: ['terminee'],
    terminee: ['decalee'],
    decalee: ['a_venir', 'annulee'],
    annulee: [],
};

export function VisiteFormContent({ prospect, visite, onSuccess, onCancel, config }) {
    const isEditing = !!visite;
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [initialFormData, setInitialFormData] = useState(null);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [commerciaux, setCommerciaux] = useState([]);
    const [prospectConfig, setProspectConfig] = useState(config || { sources: [], departements: [], ascenseur_options: [] });

    // Read prefill data once on mount (before any effect runs twice in StrictMode)
    const prefillRef = useRef(null);
    if (prefillRef.current === null) {
        try {
            const raw = sessionStorage.getItem('visite_prefill');
            if (raw) {
                prefillRef.current = JSON.parse(raw);
                sessionStorage.removeItem('visite_prefill');
            } else {
                prefillRef.current = {};
            }
        } catch {
            prefillRef.current = {};
        }
    }

    // Lecture unique (idempotente sous StrictMode) du flag de replanification
    const replanifRef = useRef(null);
    if (replanifRef.current === null) {
        try {
            const flag = sessionStorage.getItem('visite_replanif_status');
            replanifRef.current = flag || '';
            if (flag) sessionStorage.removeItem('visite_replanif_status');
        } catch {
            replanifRef.current = '';
        }
    }

    const [formData, setFormData] = useState({
        date: null, heure: '', type_visite: '', commercial_id: '', statut: 'a_venir',
        client_nom: '', telephone: '', email: '', source: '', departement: '', remarques: '',
        adresse_depart: emptyAddress(),
        adresse_arrivee: emptyAddress(),
    });
    const [datePopoverOpen, setDatePopoverOpen] = useState(false);
    const [sendConfirmEmail, setSendConfirmEmail] = useState(null); // legacy, non utilisé mais gardé pour compat
    const [showEmailConfirmModal, setShowEmailConfirmModal] = useState(false);
    const [showEmailEditModal, setShowEmailEditModal] = useState(false);
    const [emailCivilite, setEmailCivilite] = useState('M.');
    const [emailDestinataire, setEmailDestinataire] = useState('');
    const [emailSujet, setEmailSujet] = useState('');
    const [emailMessage, setEmailMessage] = useState('');
    const [sendingEmail, setSendingEmail] = useState(false);

    useEffect(() => {
        axios.get(`${API}/api/visites/config`, { withCredentials: true })
            .then(({ data }) => setCommerciaux(data.commerciaux || []))
            .catch(() => setCommerciaux([]));

        axios.get(`${API}/api/prospects/config`, { withCredentials: true })
            .then(({ data }) => setProspectConfig(data))
            .catch(() => {});

        let data;
        if (visite) {
            // Si on vient d'une replanification depuis "À reprogrammer",
            // le ref a été initialisé au montage. Dans ce cas on vide aussi
            // la date et l'heure pour forcer la saisie de nouvelles valeurs.
            // Le statut reste affiché "À reprogrammer" pendant la saisie ;
            // il ne basculera vers "a_venir" qu'au moment de la validation.
            const replanifStatus = replanifRef.current || null;
            const isReplanif = !!replanifStatus;

            data = {
                date: isReplanif ? null : (visite.date ? new Date(visite.date + 'T00:00:00') : null),
                heure: isReplanif ? '' : (visite.heure || ''),
                type_visite: visite.type_visite || '',
                commercial_id: visite.commercial_id || '',
                statut: visite.statut || 'a_venir',
                client_nom: visite.prospect_nom || '',
                telephone: visite.prospect_telephone || '',
                email: visite.prospect_email || '',
                source: visite.prospect_source || '',
                departement: visite.prospect_departement || '',
                remarques: visite.prospect_remarques || '',
                adresse_depart: visite.prospect_adresse_depart || emptyAddress(),
                adresse_arrivee: visite.prospect_adresse_arrivee || emptyAddress(),
            };
        } else {
            const p = prospect || {};
            const prefill = prefillRef.current || {};
            data = {
                date: prefill.date ? new Date(prefill.date + 'T00:00:00') : null,
                heure: prefill.heure || '',
                type_visite: prefill.type_visite || '',
                commercial_id: '', statut: 'a_venir',
                client_nom: p.client_nom || '', telephone: p.telephone || '', email: p.email || '',
                source: p.source || '', departement: p.departement || '', remarques: p.remarques || '',
                adresse_depart: p.adresse_depart || emptyAddress(),
                adresse_arrivee: p.adresse_arrivee || emptyAddress(),
            };
        }
        setFormData(data);
        setInitialFormData(JSON.stringify({ ...data, date: data.date?.toISOString() || null }));
        setEmailDestinataire(data.email || '');
        // En édition, la décision email a déjà été prise → ne pas forcer le choix à nouveau
        setSendConfirmEmail(visite ? false : null);
    }, [visite, prospect]);

    const buildConfirmationMessage = (civ, nom) => {
        return `Bonjour ${civ} ${nom},

À la suite de notre échange téléphonique, nous avons le plaisir de vous confirmer la planification d'un rendez-vous avec l'un de nos conseillers. Cette démarche est proposée à titre informatif et ne constitue aucun engagement de votre part.

Afin de vous permettre de mieux découvrir notre entreprise et la qualité de nos services, vous trouverez ci-joint une présentation succincte de notre société. Nous vous invitons également à consulter notre site internet : www.bigdemenagement.fr

Nous restons naturellement à votre entière disposition pour toute information complémentaire et vous prions d'agréer l'expression de nos salutations distinguées.

L'équipe Big Déménagement`;
    };

    const initEmailFields = () => {
        const msg = buildConfirmationMessage(emailCivilite, formData.client_nom);
        setEmailSujet(`Confirmation RDV - ${formData.client_nom || 'Client'}`);
        setEmailMessage(msg);
        setEmailDestinataire(formData.email || '');
    };

    const hasChanges = () => {
        if (!initialFormData) return false;
        const current = JSON.stringify({ ...formData, date: formData.date?.toISOString() || null });
        return current !== initialFormData;
    };

    const handleCancel = () => {
        if (hasChanges()) { setShowCancelConfirm(true); } else { onCancel(); }
    };

    const updateAddress = (type, field, value) => {
        setFormData(prev => ({ ...prev, [type]: { ...prev[type], [field]: value } }));
    };

    const isSectionComplete = {
        dateHeure: () => !!formData.date && !!formData.heure.trim(),
        typeVisite: () => !!formData.type_visite && !!formData.commercial_id,
        infos: () => !!formData.client_nom.trim() && !!formData.telephone.trim() && !!formData.email.trim(),
        source: () => !!formData.source,
        adresseDepart: () => {
            const a = formData.adresse_depart;
            return a && !!a.date && !!a.adresse && !!a.etage && !!a.ascenseur && !!a.portage && a.autorisation_stationnement !== null && a.monte_meuble !== null;
        },
        adresseArrivee: () => {
            const a = formData.adresse_arrivee;
            return a && !!a.date && !!a.adresse && !!a.etage && !!a.ascenseur && !!a.portage && a.autorisation_stationnement !== null && a.monte_meuble !== null;
        },
        remarques: () => !!formData.remarques.trim(),
    };

    const SectionLine = ({ isComplete }) => (
        <div className={`w-1 rounded-full self-stretch shrink-0 ${isComplete ? 'bg-emerald-500' : 'bg-red-400'}`} />
    );
    const SectionCheck = ({ isComplete }) => (
        isComplete ? <CheckCircle2 size={16} className="text-emerald-500 shrink-0" /> : null
    );

    const [showAnnulerDialog, setShowAnnulerDialog] = useState(false);

    const handleStatutChange = (val) => {
        // En édition : passage à "annulée" → modale obligatoire
        if (isEditing && val === 'annulee' && formData.statut !== 'annulee') {
            setShowAnnulerDialog(true);
            return;
        }
        setFormData(prev => ({ ...prev, statut: val }));
    };

    const buildPayload = () => ({
        date: format(formData.date, 'yyyy-MM-dd'),
        heure: formData.heure,
        type_visite: formData.type_visite,
        commercial_id: formData.commercial_id || "",
        statut: formData.statut,
        prospect_nom: formData.client_nom,
        prospect_telephone: formData.telephone,
        prospect_email: formData.email,
        prospect_source: formData.source,
        prospect_departement: formData.departement,
        prospect_adresse_depart: formData.adresse_depart.adresse ? formData.adresse_depart : null,
        prospect_adresse_arrivee: formData.adresse_arrivee.adresse ? formData.adresse_arrivee : null,
        prospect_remarques: formData.remarques,
    });

    const saveAndClose = async (emailChoice = null) => {
        // Flow:
        // 1. Click "Créer la visite" → creation immédiate (emailChoice = 'create-only')
        // 2. Après succès → ouverture du modal "Envoyer un email de confirmation ?"
        // 3. Si Oui → ouverture du modal email editable → envoi + fermeture

        // Envoi de l'email uniquement (cas où la visite a déjà été créée)
        if (emailChoice === 'send-email-only') {
            if (!emailDestinataire) {
                toast.error("Destinataire email manquant");
                return;
            }
            setIsSubmitting(true);
            try {
                const dateStr = formData.date ? format(formData.date, 'dd/MM/yyyy', { locale: fr }) : '';
                await axios.post(`${API}/api/visites/send-confirmation-email`, {
                    destinataire: emailDestinataire,
                    sujet: emailSujet,
                    message: emailMessage,
                    civilite: emailCivilite,
                    type_visite: formData.type_visite,
                    date_visite: dateStr,
                    heure: formData.heure,
                }, { withCredentials: true });
                toast.success('Email de confirmation envoyé');
            } catch (emailErr) {
                toast.error(emailErr.response?.data?.detail || "Erreur lors de l'envoi de l'email");
            } finally {
                setIsSubmitting(false);
                setShowEmailEditModal(false);
                setShowEmailConfirmModal(false);
                onSuccess();
            }
            return;
        }

        // Création/modification de la visite (sans email)
        if (!formData.date) { toast.error('La date est obligatoire'); setShowCancelConfirm(false); return; }
        if (!formData.heure.trim()) { toast.error("L'heure est obligatoire"); setShowCancelConfirm(false); return; }
        if (!formData.type_visite) { toast.error('Le type de visite est obligatoire'); setShowCancelConfirm(false); return; }

        setIsSubmitting(true);
        try {
            const payload = buildPayload();
            if (isEditing) {
                // Détection : si la visite était en "À reprogrammer" (decalee) et
                // qu'on la fait passer en "À venir" (a_venir) avec une nouvelle date,
                // il s'agit d'une re-planification : on ajoute une note dans les
                // remarques pour garder la trace.
                const isReplanifying = !!replanifRef.current && visite?.statut === 'decalee';
                if (isReplanifying) {
                    // Force le statut à "a_venir" même si l'UI affiche encore "decalee"
                    payload.statut = 'a_venir';
                    const fmt = (iso) => {
                        if (!iso) return '?';
                        try {
                            const [y, m, d] = String(iso).slice(0, 10).split('-');
                            return `${d}/${m}/${y}`;
                        } catch { return iso; }
                    };
                    const note = `[Visite reprogrammée] ${fmt(visite.date)} ${visite.heure || '?'} → ${fmt(payload.date)} ${payload.heure || '?'}`;
                    payload.prospect_remarques = formData.remarques
                        ? `${formData.remarques}\n${note}`
                        : note;
                }
                await axios.put(`${API}/api/visites/${visite.id}`, payload, { withCredentials: true });
                toast.success(isReplanifying ? 'Visite reprogrammée avec succès' : 'Visite modifiée avec succès');
                setShowCancelConfirm(false);

                // Marquer la tâche "Reprogrammer la visite" comme traitée si elle existe
                if (isReplanifying) {
                    try {
                        const { data: taches } = await axios.get(`${API}/api/taches`, { withCredentials: true });
                        const liee = (taches || []).find(t => t.linked_visite_id === visite.id && t.type_rappel === 'visite_reprogrammer' && t.statut !== 'traite');
                        if (liee) {
                            await axios.put(`${API}/api/taches/${liee.id}`, { statut: 'traite' }, { withCredentials: true });
                        }
                    } catch { /* non bloquant */ }
                }

                // Après une modification (même replanification), on ne re-propose
                // plus l'envoi d'email de confirmation : l'utilisateur peut le déclencher
                // manuellement depuis la fiche visite si besoin.
                onSuccess();
                return;
            }
            payload.prospect_id = prospect.id;
            await axios.post(`${API}/api/visites`, payload, { withCredentials: true });
            toast.success('Visite créée avec succès');
            setShowCancelConfirm(false);
            // Après création → demander si on envoie un email de confirmation
            setShowEmailConfirmModal(true);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
            setShowCancelConfirm(false);
        } finally { setIsSubmitting(false); }
    };

    const prospectName = prospect?.nom || visite?.prospect_nom || '';

    const DatePickerField = ({ label, value, onChange, disabledFn }) => {
        const [dpOpen, setDpOpen] = useState(false);
        const dateValue = value ? new Date(value) : null;
        return (
            <div className="flex items-center gap-4">
                <Label className="text-slate-600 w-28 md:w-72 shrink-0">{label}</Label>
                <Popover open={dpOpen} onOpenChange={setDpOpen}>
                    <PopoverTrigger asChild>
                        <Button variant="outline" className="max-w-sm ml-auto w-full justify-start text-left font-normal">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {dateValue ? format(dateValue, 'dd/MM/yyyy', { locale: fr }) : 'Sélectionner une date'}
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 z-[9999]" align="start">
                        <Calendar mode="single" selected={dateValue} onSelect={(d) => { onChange(d ? d.toISOString() : null); setDpOpen(false); }} locale={fr} disabled={disabledFn || disablePastDates} />
                    </PopoverContent>
                </Popover>
            </div>
        );
    };

    const renderAddressSection = (title, type, data, Icon, isComplete) => (
        <Card className="border-slate-200" key={type}>
            <CardHeader className="pb-3 border-b border-slate-100">
                <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                    <Icon size={16} className="text-[#2e7cd6]" />
                    {title}
                    <SectionCheck isComplete={isComplete} />
                </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
                <div className="flex gap-4">
                    <SectionLine isComplete={isComplete} />
                    <div className="flex-1 space-y-4">
                        <DatePickerField
                            label={type === 'adresse_depart' ? 'Date de chargement' : 'Date de déchargement'}
                            value={data.date}
                            onChange={(val) => updateAddress(type, 'date', val)}
                            disabledFn={
                                type === 'adresse_arrivee'
                                    ? makeDisableBeforeDateChargement(formData.adresse_depart?.date)
                                    : disablePastDates
                            }
                        />
                        <div className="flex items-center gap-4">
                            <Label className="text-slate-600 w-28 md:w-72 shrink-0">Adresse</Label>
                            <AddressAutocompleteInput
                                className="max-w-sm ml-auto"
                                value={data.adresse || ''}
                                onChange={(val) => updateAddress(type, 'adresse', val)}
                                placeholder="Saisir l'adresse complète"
                            />
                        </div>
                        <div className="flex items-center gap-4">
                            <Label className="text-slate-600 w-28 md:w-72 shrink-0">Étage</Label>
                            <Input className="max-w-sm ml-auto" value={data.etage || ''} onChange={(e) => updateAddress(type, 'etage', e.target.value)} placeholder="Ex: 3ème" />
                        </div>
                        <div className="flex items-center gap-4">
                            <Label className="text-slate-600 w-28 md:w-72 shrink-0">Portage</Label>
                            <Input className="max-w-sm ml-auto" value={data.portage || ''} onChange={(e) => updateAddress(type, 'portage', e.target.value)} placeholder="Ex: 50m" />
                        </div>
                        <div className="flex items-center gap-4">
                            <Label className="text-slate-600 w-28 md:w-72 shrink-0">Ascenseur</Label>
                            <Select value={data.ascenseur || undefined} onValueChange={(val) => updateAddress(type, 'ascenseur', val)}>
                                <SelectTrigger className="max-w-sm ml-auto"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                                <SelectContent>
                                    {(prospectConfig.ascenseur_options || []).map((opt) => (
                                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex items-center gap-4">
                            <Label className="text-slate-600 w-28 md:w-72 shrink-0">Autorisation stationnement</Label>
                            <Select
                                value={data.autorisation_stationnement === true ? 'oui' : data.autorisation_stationnement === false ? 'non' : ''}
                                onValueChange={(val) => updateAddress(type, 'autorisation_stationnement', val === 'oui' ? true : val === 'non' ? false : null)}
                            >
                                <SelectTrigger className="max-w-sm ml-auto"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="oui">Oui</SelectItem>
                                    <SelectItem value="non">Non</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex items-center gap-4">
                            <Label className="text-slate-600 w-28 md:w-72 shrink-0">Monte-meuble</Label>
                            <Select
                                value={data.monte_meuble === true ? 'oui' : data.monte_meuble === false ? 'non' : ''}
                                onValueChange={(val) => updateAddress(type, 'monte_meuble', val === 'oui' ? true : val === 'non' ? false : null)}
                            >
                                <SelectTrigger className="max-w-sm ml-auto"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="oui">Oui</SelectItem>
                                    <SelectItem value="non">Non</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );

    return (
        <>
            {/* Sticky Header */}
            <div className="sticky top-0 z-50 bg-white border-b px-4 md:px-6 py-3 md:py-4">
                <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                        <h2 className="text-base md:text-2xl font-semibold leading-tight tracking-tight truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {isEditing ? 'Modifier la visite' : 'Nouvelle visite'}
                            {prospectName && <span className="text-slate-500 font-normal text-sm md:text-lg"> — {prospectName}</span>}
                        </h2>
                        <p className="text-xs md:text-sm text-muted-foreground mt-0.5">
                            {isEditing ? 'Modifiez les informations de la visite' : 'Créez une nouvelle visite'}
                        </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <Button type="button" variant="outline" size="sm" onClick={handleCancel} data-testid="visite-cancel-btn">Annuler</Button>
                        <Button size="sm" onClick={saveAndClose} disabled={isSubmitting} className="bg-[#2e7cd6] hover:bg-blue-700" data-testid="visite-save-btn">
                            {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                            {isEditing ? 'Enregistrer' : 'Créer'}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4">
                <div className="space-y-6">

                    {/* Section 1: Statut de la visite (editing only) */}
                    {isEditing && (
                    <Card className={`bg-blue-50 border-2 ${(VISITE_STATUS_CONFIG[formData.statut] || VISITE_STATUS_CONFIG.a_venir).borderClass}`}>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-blue-800">
                                <CircleDot size={16} />
                                1. Statut de la visite
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {replanifRef.current ? (
                                <div className="flex flex-col gap-2">
                                    <span className={`inline-block w-fit px-3 py-1.5 rounded-lg text-sm font-semibold border ${VISITE_STATUS_CONFIG.decalee.badgeClass} ${VISITE_STATUS_CONFIG.decalee.borderClass}`} data-testid="visite-statut-replanif-badge">
                                        {VISITE_STATUS_CONFIG.decalee.label}
                                    </span>
                                    <p className="text-xs text-orange-700 italic">
                                        Saisissez la nouvelle date et l'heure ci-dessous. Le statut passera automatiquement à <strong>À venir</strong> une fois la visite enregistrée.
                                    </p>
                                </div>
                            ) : (
                                <Select value={formData.statut} onValueChange={handleStatutChange}>
                                    <SelectTrigger className="w-full md:w-[300px] bg-white" data-testid="visite-statut-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {(() => {
                                            // Edition : on n'autorise que le statut actuel + transitions valides.
                                            // Création : tous les statuts (formData.statut par défaut = 'a_venir').
                                            const cur = formData.statut;
                                            const allowed = isEditing
                                                ? [cur, ...(VISITE_TRANSITIONS[cur] || [])]
                                                : Object.keys(VISITE_STATUS_CONFIG);
                                            return allowed.map((key) => {
                                                const cfg = VISITE_STATUS_CONFIG[key];
                                                if (!cfg) return null;
                                                // Quand on est en "À reprogrammer", l'option "À venir" est libellée "Nouvelle date"
                                                const displayLabel = (cur === 'decalee' && key === 'a_venir') ? 'Nouvelle date' : cfg.label;
                                                return (
                                                    <SelectItem key={key} value={key}>
                                                        <span className={`inline-block px-2 py-0.5 rounded text-sm ${cfg.badgeClass}`}>{displayLabel}</span>
                                                    </SelectItem>
                                                );
                                            });
                                        })()}
                                    </SelectContent>
                                </Select>
                            )}
                        </CardContent>
                    </Card>
                    )}

                    {/* Section: Type de visite et Commercial */}
                    <Card className="border-slate-200">
                        <CardHeader className="pb-2 border-b border-slate-100">
                            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                                <MapPin size={16} className="text-[#2e7cd6]" />
                                {isEditing ? '2' : '1'}. Type de visite et Commercial
                                <SectionCheck isComplete={isSectionComplete.typeVisite()} />
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-4">
                            <div className="flex gap-4">
                                <SectionLine isComplete={isSectionComplete.typeVisite()} />
                                <div className="flex-1 space-y-4">
                                    <div className="flex items-center gap-4">
                                        <Label className="text-slate-600 w-28 md:w-72 shrink-0">Type *</Label>
                                        <Select key={`type-${formData.type_visite}`} value={formData.type_visite} onValueChange={(v) => setFormData(prev => ({ ...prev, type_visite: v }))}>
                                            <SelectTrigger className="max-w-sm ml-auto" data-testid="visite-type-select"><SelectValue placeholder="Sélectionner un type" /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="virtuelle">Virtuelle</SelectItem>
                                                <SelectItem value="presentielle">Présentielle</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <Label className="text-slate-600 w-28 md:w-72 shrink-0">Commercial</Label>
                                        <Select value={formData.commercial_id} onValueChange={(v) => setFormData(prev => ({ ...prev, commercial_id: v === '__none__' ? '' : v }))}>
                                            <SelectTrigger className="max-w-sm ml-auto" data-testid="visite-commercial-select"><SelectValue placeholder="Non affecté" /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="__none__">
                                                    <div className="flex items-center gap-2">
                                                        <span className="w-3 h-3 rounded-full bg-slate-300 inline-block" />
                                                        Non affecté
                                                    </div>
                                                </SelectItem>
                                                {commerciaux.map((c) => (
                                                    <SelectItem key={c.id} value={c.id}>
                                                        <div className="flex items-center gap-2">
                                                            <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: c.couleur || '#2e7cd6' }} />
                                                            {c.name}
                                                        </div>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Section: Date & Heure */}
                    <Card className="border-slate-200">
                        <CardHeader className="pb-2 border-b border-slate-100">
                            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                                <CalendarIcon size={16} className="text-[#2e7cd6]" />
                                {isEditing ? '3' : '2'}. Date et heure
                                <SectionCheck isComplete={isSectionComplete.dateHeure()} />
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-4">
                            <div className="flex gap-4">
                                <SectionLine isComplete={isSectionComplete.dateHeure()} />
                                <div className="flex-1 divide-y divide-slate-100">
                                    <div className="flex items-center gap-4 py-3">
                                        <p className="text-sm text-slate-500 w-[140px] md:w-[460px] shrink-0">Date *</p>
                                        <Popover open={datePopoverOpen} onOpenChange={setDatePopoverOpen}>
                                            <PopoverTrigger asChild>
                                                <Button variant="outline" className="max-w-sm w-full ml-auto justify-start text-left font-normal" data-testid="visite-date-trigger">
                                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                                    {formData.date ? format(formData.date, 'dd MMMM yyyy', { locale: fr }) : 'Sélectionner une date'}
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-auto p-0 z-[9999]" align="start">
                                                <Calendar mode="single" selected={formData.date} onSelect={(d) => { setFormData(prev => ({ ...prev, date: d })); setDatePopoverOpen(false); }} locale={fr} disabled={disablePastDates} />
                                            </PopoverContent>
                                        </Popover>
                                    </div>
                                    <div className="flex items-center gap-4 py-3">
                                        <p className="text-sm text-slate-500 w-[140px] md:w-[460px] shrink-0">Heure *</p>
                                        <div className="max-w-sm w-full ml-auto relative">
                                            <Clock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                            <Input type="time" value={formData.heure} onChange={(e) => setFormData(prev => ({ ...prev, heure: e.target.value }))} className="pl-10" data-testid="visite-heure-input" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* La confirmation email n'est plus une section du formulaire : elle apparaît dans un modal au moment de la création/modification. */}

                    {/* Section: Informations client */}
                    <Card className="border-slate-200">
                        <CardHeader className="pb-3 border-b border-slate-100">
                            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                                <User size={16} className="text-[#2e7cd6]" />
                                {isEditing ? '4' : '3'}. Informations client
                                <SectionCheck isComplete={isSectionComplete.infos()} />
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-4">
                            <div className="flex gap-4">
                                <SectionLine isComplete={isSectionComplete.infos()} />
                                <div className="flex-1 space-y-4">
                                    <div className="flex items-center gap-4">
                                        <Label className="text-slate-600 w-28 md:w-72 shrink-0">Nom *</Label>
                                        <Input className="max-w-sm ml-auto" value={formData.client_nom} onChange={(e) => setFormData(prev => ({ ...prev, client_nom: e.target.value }))} placeholder="Nom du client" data-testid="visite-nom" />
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <Label className="text-slate-600 w-28 md:w-72 shrink-0">Téléphone</Label>
                                        <Input className="max-w-sm ml-auto" value={formData.telephone} onChange={(e) => setFormData(prev => ({ ...prev, telephone: e.target.value }))} placeholder="06 00 00 00 00" data-testid="visite-telephone" />
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <Label className="text-slate-600 w-28 md:w-72 shrink-0">Email</Label>
                                        <Input className="max-w-sm ml-auto" type="email" value={formData.email} onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))} placeholder="email@exemple.fr" data-testid="visite-email" />
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Section: Source & Département */}
                    <Card className="border-slate-200">
                        <CardHeader className="pb-3 border-b border-slate-100">
                            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                                <Globe size={16} className="text-[#2e7cd6]" />
                                {isEditing ? '5' : '4'}. Source & Département
                                <SectionCheck isComplete={isSectionComplete.source()} />
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-4">
                            <div className="flex gap-4">
                                <SectionLine isComplete={isSectionComplete.source()} />
                                <div className="flex-1 space-y-4">
                                    <div className="flex items-center gap-4">
                                        <Label className="text-slate-600 w-28 md:w-72 shrink-0">Source</Label>
                                        <Select value={formData.source} onValueChange={(val) => setFormData(prev => ({ ...prev, source: val }))}>
                                            <SelectTrigger className="max-w-sm ml-auto" data-testid="visite-source"><SelectValue placeholder="Sélectionner une source" /></SelectTrigger>
                                            <SelectContent>
                                                {(prospectConfig.sources || []).map((s) => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <Label className="text-slate-600 w-28 md:w-72 shrink-0">Département</Label>
                                        <Select value={formData.departement} onValueChange={(val) => setFormData(prev => ({ ...prev, departement: val }))}>
                                            <SelectTrigger className="max-w-sm ml-auto" data-testid="visite-departement"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                                            <SelectContent>
                                                {(prospectConfig.departements || []).map((d) => (<SelectItem key={d.code} value={d.code}>{d.label}</SelectItem>))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Section: Adresse de départ */}
                    {renderAddressSection(`${isEditing ? '6' : '5'}. Adresse de départ`, 'adresse_depart', formData.adresse_depart, MapPin, isSectionComplete.adresseDepart())}

                    {/* Section: Adresse d'arrivée */}
                    {renderAddressSection(`${isEditing ? '7' : '6'}. Adresse d'arrivée`, 'adresse_arrivee', formData.adresse_arrivee, Truck, isSectionComplete.adresseArrivee())}

                    {/* Section: Remarques */}
                    <Card className="border-slate-200">
                        <CardHeader className="pb-3 border-b border-slate-100">
                            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                                <FileText size={16} className="text-[#2e7cd6]" />
                                {isEditing ? '8' : '7'}. Remarques
                                <SectionCheck isComplete={isSectionComplete.remarques()} />
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-4">
                            <div className="flex gap-4">
                                <SectionLine isComplete={isSectionComplete.remarques()} />
                                <div className="flex-1">
                                    <Textarea
                                        value={formData.remarques}
                                        onChange={(e) => setFormData(prev => ({ ...prev, remarques: e.target.value }))}
                                        placeholder="Notes et remarques..."
                                        rows={6}
                                        className="w-full min-h-[120px] resize-y"
                                        data-testid="visite-remarques"
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                </div>
            </div>

            <AlertDialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {isEditing ? 'Modifications non enregistrées' : 'Abandonner la création ?'}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {isEditing
                                ? 'Vous avez des modifications non enregistrées. Voulez-vous les enregistrer avant de quitter ?'
                                : 'Voulez-vous vraiment abandonner la création de cette visite ? Les informations saisies seront perdues.'
                            }
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        {isEditing ? (
                            <>
                                <AlertDialogCancel onClick={() => {
                                    setShowCancelConfirm(false);
                                    onCancel();
                                }}>
                                    Ne pas enregistrer
                                </AlertDialogCancel>
                                <AlertDialogAction onClick={saveAndClose}>
                                    Enregistrer
                                </AlertDialogAction>
                            </>
                        ) : (
                            <>
                                <AlertDialogCancel onClick={() => setShowCancelConfirm(false)}>
                                    Continuer la création
                                </AlertDialogCancel>
                                <AlertDialogAction onClick={() => {
                                    setShowCancelConfirm(false);
                                    onCancel();
                                }}>
                                    Abandonner
                                </AlertDialogAction>
                            </>
                        )}
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Modal 1 : Confirmation envoi email (Oui / Non) */}
            <AlertDialog open={showEmailConfirmModal} onOpenChange={setShowEmailConfirmModal}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <Mail size={20} className="text-[#2e7cd6]" />
                            Envoyer un email de confirmation ?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            Souhaitez-vous envoyer un email de confirmation au client pour ce rendez-vous ?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="gap-2">
                        <AlertDialogAction
                            className="bg-slate-500 hover:bg-slate-600"
                            onClick={() => { setShowEmailConfirmModal(false); onSuccess(); }}
                            data-testid="visite-email-skip-btn"
                        >
                            Non, ne pas envoyer
                        </AlertDialogAction>
                        <AlertDialogAction
                            className="bg-[#2e7cd6] hover:bg-[#2568b8]"
                            onClick={() => {
                                setShowEmailConfirmModal(false);
                                initEmailFields();
                                setShowEmailEditModal(true);
                            }}
                            data-testid="visite-email-yes-btn"
                        >
                            Oui, préparer l'email
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Modal 2 : Préparation + envoi de l'email */}
            <Dialog open={showEmailEditModal} onOpenChange={setShowEmailEditModal}>
                <DialogContent className="max-w-2xl w-full max-h-[90vh] flex flex-col" data-testid="visite-email-edit-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Mail size={20} className="text-[#2e7cd6]" />
                            Email de confirmation
                        </DialogTitle>
                        <DialogDescription>
                            Vérifiez/modifiez le message avant envoi au client.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex-1 overflow-y-auto space-y-4 pr-1">
                        <div className="grid grid-cols-[120px_1fr] gap-3">
                            <div>
                                <label className="text-sm font-medium text-slate-700 mb-1 block">Civilité</label>
                                <Select value={emailCivilite} onValueChange={(val) => {
                                    setEmailCivilite(val);
                                    setEmailMessage(prev => {
                                        const old = prev.match(/^Bonjour (M\.|Mme)/);
                                        if (old) return prev.replace(/^Bonjour (M\.|Mme)/, `Bonjour ${val}`);
                                        return prev;
                                    });
                                }}>
                                    <SelectTrigger data-testid="visite-email-civilite"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="M.">M.</SelectItem>
                                        <SelectItem value="Mme">Mme</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-slate-700 mb-1 block">Destinataire</label>
                                <Input type="email" value={emailDestinataire} onChange={e => setEmailDestinataire(e.target.value)} placeholder="email@client.com" data-testid="visite-email-destinataire" />
                            </div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-slate-700 mb-1 block">Sujet</label>
                            <Input value={emailSujet} onChange={e => setEmailSujet(e.target.value)} data-testid="visite-email-sujet" />
                        </div>
                        <div>
                            <label className="text-sm font-medium text-slate-700 mb-1 block">Message</label>
                            <Textarea value={emailMessage} onChange={e => setEmailMessage(e.target.value)} rows={10} className="resize-y text-sm" data-testid="visite-email-message" />
                        </div>

                        {/* Bloc récap du RDV tel qu'il apparaîtra dans l'email */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <p className="text-xs font-semibold text-blue-700 mb-3">Bloc récapitulatif affiché dans l'email :</p>
                            <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                    {formData.type_visite === 'presentielle'
                                        ? <MapPin size={16} className="text-[#2e7cd6]" />
                                        : <Globe size={16} className="text-[#2e7cd6]" />
                                    }
                                    <span className="text-sm font-bold text-[#2e7cd6]">
                                        {formData.type_visite === 'presentielle' ? 'Visite présentielle à domicile' : formData.type_visite === 'virtuelle' ? 'Visite virtuelle (visio)' : 'Type non défini'}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <CalendarIcon size={16} className="text-[#2e7cd6]" />
                                    <span className="text-sm font-semibold text-slate-800">
                                        {formData.date ? format(formData.date, 'dd MMMM yyyy', { locale: fr }) : '---'}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Clock size={16} className="text-[#2e7cd6]" />
                                    <span className="text-sm font-semibold text-slate-800">
                                        {formData.heure ? (
                                            formData.type_visite === 'presentielle'
                                                ? `${formData.heure} - ${String(parseInt(formData.heure.split(':')[0]) + 1).padStart(2, '0')}:${formData.heure.split(':')[1] || '00'}`
                                                : formData.heure
                                        ) : '---'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Note : pièce jointe automatique */}
                        <div className="flex items-start gap-2 bg-slate-50 border border-dashed border-slate-300 rounded-lg p-3" data-testid="visite-email-presentation-notice">
                            <Paperclip size={16} className="text-[#2e7cd6] shrink-0 mt-0.5" />
                            <p className="text-xs text-slate-600 leading-relaxed">
                                Notre <strong className="text-[#2e7cd6]">document de présentation</strong> (PDF) sera automatiquement joint à cet email.
                            </p>
                        </div>
                    </div>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => { setShowEmailEditModal(false); setShowEmailConfirmModal(true); }} data-testid="visite-email-back-btn">
                            Retour
                        </Button>
                        <Button
                            className="bg-[#2e7cd6] hover:bg-[#2568b8]"
                            onClick={() => saveAndClose('send-email-only')}
                            disabled={isSubmitting || !emailDestinataire}
                            data-testid="visite-email-send-btn"
                        >
                            {isSubmitting ? <Loader2 size={16} className="animate-spin mr-1.5" /> : <Mail size={16} className="mr-1.5" />}
                            Envoyer l'email
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <VisiteAnnulerDialog
                open={showAnnulerDialog}
                onOpenChange={setShowAnnulerDialog}
                visite={visite}
                onSuccess={() => {
                    setFormData(prev => ({ ...prev, statut: 'annulee' }));
                    if (onSuccess) onSuccess();
                }}
            />
        </>
    );
}

export function VisiteFormDialog({ open, onOpenChange, prospect, visite, onSuccess, config }) {
    const lock = useEditLock('visite', visite?.id || null, !!visite?.id && open);
    if (!open) return null;
    return (
        <Dialog open={open} onOpenChange={(val) => { if (!val) onOpenChange(false); }}>
            <DialogContent
                className="max-w-5xl w-full max-h-[90vh] flex flex-col p-0"
                onPointerDownOutside={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => e.preventDefault()}
                hideCloseButton
                data-testid="visite-form-dialog"
            >
                {lock.blocked && <LockBanner userName={lock.user_name} expiresAt={lock.expires_at} onClose={() => onOpenChange(false)} />}
                <ReadOnlyGuard module="visites_calendrier" lockBlocked={lock.blocked}>
                    <VisiteFormContent
                        prospect={prospect}
                        visite={visite}
                        onSuccess={onSuccess}
                        onCancel={() => onOpenChange(false)}
                        config={config}
                    />
                </ReadOnlyGuard>
            </DialogContent>
        </Dialog>
    );
}
